import { useEffect, useState } from "react";

export const useScrollSpy = (sectionIds: string[]) => {
  const [activeHref, setActiveHref] = useState<string>(() => {
    // Initialize from current URL hash
    return window.location.hash || `#${sectionIds[0]}`;
  });

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const sectionElements = sectionIds
      .map((id) => document.getElementById(id.replace("#", "")))
      .filter((el): el is HTMLElement => el !== null);

    if (sectionElements.length === 0) return;

    // Track which sections are currently visible
    const visibleSections = new Set<string>();

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const sectionId = `#${entry.target.id}`;

        if (entry.isIntersecting) {
          visibleSections.add(sectionId);
        } else {
          visibleSections.delete(sectionId);
        }
      });

      // If there are visible sections, activate the first one in the list
      if (visibleSections.size > 0) {
        const firstVisibleSection = sectionIds.find((id) =>
          visibleSections.has(id)
        );

        if (firstVisibleSection && firstVisibleSection !== activeHref) {
          setActiveHref(firstVisibleSection);
          window.history.replaceState(null, "", firstVisibleSection);
        }
      }
    };

    // Create an observer for each section
    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: "-20% 0px -35% 0px", // Trigger when section is ~20% from top
      threshold: 0,
    });

    sectionElements.forEach((element) => {
      observer.observe(element);
      observers.push(observer);
    });

    // Handle hash changes from navigation
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash && sectionIds.includes(hash)) {
        setActiveHref(hash);
      }
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      observers.forEach((obs) => obs.disconnect());
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [sectionIds, activeHref]);

  return activeHref;
};
